import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

interface Feature {
  key: string;
  label: string;
  description: string;
  whenOff: string;
  default: boolean;
  group: string;
  enabled: boolean;
}

/**
 * 功能开关。
 *
 * ★ 这一屏最重要的不是开关本身,是**每个开关旁边那句「关掉之后会怎样」**。
 *
 *   只写「配送中状态」四个字的话,运营不知道该不该开,于是要么不敢动,
 *   要么全打开。写清「关掉后取货之后直接就是配送状态,司机少点一次」,
 *   他自己就能决定。
 *
 * 开关是即时生效的、而且没有二次确认 —— 所以文案必须在**点之前**就说清楚。
 */
export function FeatureToggles({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<Feature[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * ★ 刚存好的那一个。
   *
   *   用户原话:「打开或者关闭某个设置后在哪保存?没有看到保存按钮」——
   *   这一屏的设计是「点一下就是保存」,但界面上**没有任何地方说这件事**:
   *   开关滑过去了,然后呢?人只能猜,而最省事的猜法是
   *   「大概还要点个保存」,找不到就以为没存上。
   *   存完说一句,这个疑问就不存在了。
   */
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api<Feature[]>('/features'));
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '读不到功能开关');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(f: Feature) {
    if (busy) return;
    setBusy(f.key);
    setError(null);
    try {
      setRows(
        await api<Feature[]>('/features', {
          method: 'PATCH',
          body: { features: { [f.key]: !f.enabled } },
        }),
      );
      setSaved(f.key);
      // 两秒后收起来 —— 它是一次确认,不是一条要一直挂着的状态
      setTimeout(() => setSaved((k) => (k === f.key ? null : k)), 2_000);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '改不动');
    } finally {
      setBusy(null);
    }
  }

  const groups = [...new Set(rows.map((r) => r.group))];

  return (
    <section>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-1">功能开关</h2>
      <p className="mb-4 text-sm text-slate-500">
        {/* ★ 把「不用另外保存」说在最前面 —— 这是人打开这一屏第一个会问的事 */}
        <span className="font-medium text-slate-600 dark:text-slate-300">
          点一下就已经保存,这一屏没有保存按钮。
        </span>{' '}
        改完立刻生效,没有二次确认 —— 每个开关下面写了关掉之后会怎样,点之前先看一眼。
      </p>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g}>
            <h3 className="mb-2 text-sm font-medium text-slate-500">{g}</h3>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700
                            bg-white dark:bg-slate-800 divide-y divide-slate-100
                            dark:divide-slate-700/50">
              {rows
                .filter((r) => r.group === g)
                .map((f) => (
                  <div key={f.key} className="p-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-slate-900 dark:text-slate-100">
                        {f.label}
                        {saved === f.key && (
                          <span
                            aria-live="polite"
                            className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs
                                       text-green-700 dark:bg-green-950 dark:text-green-300"
                          >
                            已保存
                          </span>
                        )}
                        {!f.enabled && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-slate-100
                                           dark:bg-slate-700 text-slate-500">
                            已关闭
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-sm text-slate-500">
                        {/* ★ 开着时说它是什么,关着时说关掉的后果 */}
                        {f.enabled ? f.description : f.whenOff}
                      </div>
                    </div>
                    <button
                      role="switch"
                      aria-checked={f.enabled}
                      aria-label={f.label}
                      disabled={!canManage || busy === f.key}
                      onClick={() => void toggle(f)}
                      className={`shrink-0 w-12 h-7 rounded-full transition relative
                                  disabled:opacity-40 ${
                                    f.enabled
                                      ? 'bg-brand-600'
                                      : 'bg-slate-300 dark:bg-slate-600'
                                  }`}
                    >
                      <span
                        className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                          f.enabled ? 'left-6' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>

      {!canManage && rows.length > 0 && (
        <p className="mt-2 text-xs text-slate-400">
          改开关需要租户管理权限 —— 一个开关能改变全租户的行为,能改的人要少。
        </p>
      )}
    </section>
  );
}
